import React, { useEffect } from 'react';
import { ScrollText, X } from 'lucide-react';
import { cn } from '../../poker-ui/utils/cn';
import { belotMatchCpFromEnginePoints, belotMatchTargetCpFromEngine } from '../../game/belotArmenianCards';

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {number} props.targetScore
 * @param {Array<{ roundOrdinal: number, deltaUs: number, deltaThem: number, cumUs: number, cumThem: number }>} props.rows
 * @param {string} [props.title]
 */
export function ArmenianBelotScoreSheetPanel({
  open,
  onClose,
  targetScore,
  rows,
  title = 'Листок партии',
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const fmtDelta = (n) => {
    const eng = Number(n) || 0;
    if (eng === 0) return '0';
    const s = belotMatchCpFromEnginePoints(eng);
    if (s === '—') return '—';
    return eng > 0 ? `+${s}` : s;
  };

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-[70] bg-black/55 backdrop-blur-[2px]"
        aria-label="Закрыть листок"
        onClick={onClose}
      />
      <div
        className={cn(
          'fixed z-[71] flex max-h-[min(92vh,640px)] flex-col border-l border-amber-900/40 bg-[#f4ecd8] text-[#2c2416] shadow-[-12px_0_48px_rgba(0,0,0,0.35)]',
          'inset-y-0 right-0 w-full max-w-[min(100vw,22rem)] sm:max-w-md',
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="armenian-score-sheet-title"
      >
        <div className="flex items-start justify-between gap-2 border-b border-amber-900/25 bg-[#ebe2c8] px-3 py-2.5 sm:px-4">
          <div className="flex min-w-0 items-center gap-2">
            <ScrollText className="h-5 w-5 shrink-0 text-amber-900/70" aria-hidden />
            <div className="min-w-0">
              <h2 id="armenian-score-sheet-title" className="font-serif text-base font-bold leading-tight text-amber-950">
                {title}
              </h2>
              <p className="mt-0.5 font-mono text-[10px] text-amber-900/65">
                Игра до {belotMatchTargetCpFromEngine(targetScore)} CP (матч)
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-amber-900/20 bg-white/40 text-amber-950 transition hover:bg-white/70"
            aria-label="Закрыть"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-3 sm:px-3">
          {rows.length === 0 ? (
            <p className="px-2 text-center font-serif text-sm italic text-amber-900/55">
              После каждого раунда здесь появятся записи, как на бумажке…
            </p>
          ) : (
            <div className="rounded border border-amber-900/30 bg-[#faf6ea] shadow-[inset_0_0_0_1px_rgba(139,90,43,0.08)]">
              <table className="w-full border-collapse text-left text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-amber-900/25 bg-[#e8dcc4] font-serif">
                    <th className="px-1.5 py-2 font-bold tabular-nums sm:px-2">#</th>
                    <th className="px-1.5 py-2 font-bold sm:px-2">Мы</th>
                    <th className="px-1.5 py-2 font-bold sm:px-2">Они</th>
                    <th className="hidden px-1.5 py-2 font-bold tabular-nums sm:table-cell sm:px-2">Σ мы</th>
                    <th className="hidden px-1.5 py-2 font-bold tabular-nums sm:table-cell sm:px-2">Σ они</th>
                  </tr>
                </thead>
                <tbody className="font-mono tabular-nums">
                  {rows.map((r) => (
                    <tr key={r.roundOrdinal} className="border-b border-amber-900/15 last:border-0">
                      <td className="px-1.5 py-1.5 text-amber-900/80 sm:px-2">{r.roundOrdinal}</td>
                      <td className="px-1.5 py-1.5 sm:px-2">{fmtDelta(r.deltaUs)}</td>
                      <td className="px-1.5 py-1.5 sm:px-2">{fmtDelta(r.deltaThem)}</td>
                      <td className="hidden px-1.5 py-1.5 font-semibold text-amber-950 sm:table-cell sm:px-2">
                        {belotMatchCpFromEnginePoints(r.cumUs)}
                      </td>
                      <td className="hidden px-1.5 py-1.5 font-semibold text-amber-950 sm:table-cell sm:px-2">
                        {belotMatchCpFromEnginePoints(r.cumThem)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="border-t border-amber-900/20 px-2 py-1.5 font-mono text-[10px] text-amber-900/55 sm:hidden">
                Σ мы / Σ они — поверните экран или разверните окно.
              </p>
            </div>
          )}
        </div>

        {rows.length > 0 ? (
          <div className="border-t border-amber-900/25 bg-[#ebe2c8] px-3 py-2.5 font-mono text-xs text-amber-950 sm:px-4">
            <div className="flex justify-between gap-4">
              <span className="text-amber-900/70">Сейчас</span>
                <span className="font-bold tabular-nums">
                {belotMatchCpFromEnginePoints(rows[rows.length - 1].cumUs)} :{' '}
                {belotMatchCpFromEnginePoints(rows[rows.length - 1].cumThem)}
                <span className="ml-1.5 font-normal text-amber-900/60">
                  / {belotMatchTargetCpFromEngine(targetScore)} CP
                </span>
              </span>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}

/**
 * Кнопка открытия листка (для хедера / панели управления).
 * @param {string} [props.className]
 */
export function ArmenianBelotScoreSheetTrigger({ onClick, disabled, label = 'Листок', className }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title="История очков по раундам"
      className={cn(
        'inline-flex items-center gap-1 rounded-lg border px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide transition sm:text-xs',
        disabled
          ? 'cursor-not-allowed border-white/10 text-muted-foreground opacity-45'
          : 'border-amber-400/35 bg-amber-950/25 text-amber-100 hover:border-amber-300/50 hover:bg-amber-900/35',
        className,
      )}
    >
      <ScrollText className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" aria-hidden />
      {label}
    </button>
  );
}
